/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import { OVERLAY_TIMEOUT } from '../../config';
import { InteractiveComponents } from '../../core/enums';
import { Log } from '../../core/logging';
import { TOverlayOptions } from '../../core/types';
import {
    clearActiveInteractiveComponent,
    setActiveInteractiveComponent,
} from '../interactivelayer/interactiveLayer.reducers';
import {
    setOverlayHighlightHorizontalLimitAction,
    setOverlayHighlightHorizontalPositionAction,
    setOverlayHighlightVerticalLimitAction,
    setOverlayHighlightVerticalPositionAction,
} from '../overlay/overlayhighlight/overlayhighlight.actions';
import {
    overlayDisableGhostMode,
    overlayEnableGhostMode,
} from '../overlay/overlayhighlight/overlayhighlight.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';
import { setScreenTaskController } from '../screens/screens.reducers';
import {
    overlayDialogSetIsCaretVisibleAction,
    setOverlayDialogOptionsAction,
    setOverlayDialogStateAction,
    setOverlayDialogTimerIdAction,
} from './overlaydialog.actions';
import {
    OVERLAY_DIALOG_SET_IS_CARET_VISIBLE,
    SET_OVERLAY_DIALOG_OPTIONS,
    SET_OVERLAY_DIALOG_STATE,
    SET_OVERLAY_DIALOG_TIMER_ID,
} from './overlaydialog.types';
import { IOverlayDialog } from './overlaydialogprops';

const TAG: string = 'overlaydialog';

const defaultUIProperties: IOverlayDialog = {
    description: '',
    enableCaret: false,
    errorPriority: 0,
    hasDimmer: true,
    hasSeperator: false,
    iconURL: '',
    isPersistent: false,
    messageCode: '',
    mouseTrapValue: '',
    options: [],
    overlayType: OverlayTypes.VOID,
    subtitle: '',
    title: '',
    verticalPositionCaretAvailable: [],
    onAutoHide: () => null,
};

const initialState: {
    isCaretVisible: boolean;
    isOverlayDialogActive: boolean;
    timerId: any;
    uiProperties: IOverlayDialog;
} = {
    isCaretVisible: false,
    isOverlayDialogActive: false,
    timerId: null,
    uiProperties: defaultUIProperties,
};

const overlayDialogReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case OVERLAY_DIALOG_SET_IS_CARET_VISIBLE:
            return {
                ...state,
                isCaretVisible: payload.isCaretVisible,
            };
        case SET_OVERLAY_DIALOG_STATE:
            return {
                ...state,
                uiProperties: payload.uiProperties,
                isOverlayDialogActive: payload.isOverlayDialogActive,
            };
        case SET_OVERLAY_DIALOG_TIMER_ID:
            return {
                ...state,
                timerId: payload.timerId,
            };
        case SET_OVERLAY_DIALOG_OPTIONS:
            return {
                ...state,
                uiProperties: {
                    ...state.uiProperties,
                    options: payload.options,
                },
            };
        default:
            return state;
    }
};

export const setOverlayDialogOptions = (options: TOverlayOptions[]) => (dispatch: Function) => {
    dispatch(setOverlayDialogOptionsAction(options));
};

export const showOverlayDialog =
    (props: IOverlayDialog) => (dispatch: Function, getState: Function) => {
        const horizontalPositions: number[] = [];
        const horizontalPositionLimits: number[] = [];
        if (props.maxInputLength) {
            horizontalPositions[0] = 0;
            horizontalPositionLimits[0] = props.maxInputLength;
        } else {
            props.options.forEach((option: TOverlayOptions, index) => {
                horizontalPositions[index] = option.initialHorizontalIndex || 0;
                horizontalPositionLimits[index] = option.horizontalSelectionoptions?.length || 0;
            });
        }
        dispatch(setOverlayDialogStateAction(props, true));
        dispatch(setOverlayHighlightVerticalPositionAction(props?.initialVerticalIndex ?? 0));
        dispatch(setOverlayHighlightVerticalLimitAction(props.options?.length));
        dispatch(setOverlayHighlightHorizontalPositionAction(horizontalPositions));
        dispatch(setOverlayHighlightHorizontalLimitAction(horizontalPositionLimits));
        const { uiProperties } = getState().overlayDialog;
        if (!uiProperties.isPersistent) {
            const timerId = setTimeout(() => {
                dispatch(hideOverlayDialog());
                try {
                    uiProperties.onAutoHide();
                } catch (error) {
                    Log.e(TAG, 'onAutoHide is undefined', error);
                }
            }, OVERLAY_TIMEOUT);
            dispatch(setOverlayDialogTimerIdAction(timerId));
        }
    };

export const hideOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { timerId, isOverlayDialogActive } = getState().overlayDialog;
    const { previousScreenTaskController } = getState().screens;
    const { isOverlayGhostHighlightActive } = getState().overlayHighlight;
    if (!isOverlayDialogActive) return;
    dispatch(setScreenTaskController(previousScreenTaskController));
    clearTimeout(timerId);
    dispatch(resetOverlayDialogState());
    dispatch(overlayDialogSetIsCaretVisibleAction(false));
    if (isOverlayGhostHighlightActive) dispatch(overlayDisableGhostMode());
};

export const resetOverlayDialogState = () => (dispatch: Function) => {
    dispatch(setOverlayDialogStateAction(defaultUIProperties, false));
};

export const onKeyDownOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, verticalLimit } = getState().overlayHighlight;
    const { options }: { options: TOverlayOptions } = getState().overlayDialog.uiProperties;
    let newVerticalPosition = verticalPosition + 1;
    let newOption = options[newVerticalPosition];
    while (newOption?.disabled) {
        newVerticalPosition += 1;
        newOption = options[newVerticalPosition];
    }
    if (newVerticalPosition < verticalLimit)
        dispatch(setOverlayHighlightVerticalPositionAction(newVerticalPosition));
};

export const onKeyLeftOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition, horizontalPosition, horizontalLimit } = getState().overlayHighlight;
    if (horizontalLimit[verticalPosition] > 1) {
        const updatedHorizontalPositions = horizontalPosition;
        updatedHorizontalPositions[verticalPosition] =
            (horizontalPosition[verticalPosition] - 1 + horizontalLimit[verticalPosition]) %
            horizontalLimit[verticalPosition];
        dispatch(setOverlayHighlightHorizontalPositionAction(updatedHorizontalPositions));
    }
};

export const onKeyRightOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { horizontalPosition, horizontalLimit, verticalPosition } = getState().overlayHighlight;
    if (horizontalLimit[verticalPosition] > 1) {
        const updatedHorizontalPositions = horizontalPosition;
        updatedHorizontalPositions[verticalPosition] =
            (horizontalPosition[verticalPosition] + 1) % horizontalLimit[verticalPosition];
        dispatch(setOverlayHighlightHorizontalPositionAction(updatedHorizontalPositions));
    }
};

export const onKeyUpOverlayDialog = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition } = getState().overlayHighlight;
    const { options }: { options: TOverlayOptions } = getState().overlayDialog.uiProperties;
    let newVerticalPosition = verticalPosition - 1;
    let newOption = options[newVerticalPosition];
    while (newOption?.disabled) {
        newVerticalPosition -= 1;
        newOption = options[newVerticalPosition];
    }
    if (newVerticalPosition >= 0)
        dispatch(setOverlayHighlightVerticalPositionAction(newVerticalPosition));
};

export const onKeyEventResetOverlayDialogTimer = () => (dispatch: Function, getState: Function) => {
    const { timerId, isOverlayDialogActive, uiProperties } = getState().overlayDialog;
    clearTimeout(timerId);
    if (isOverlayDialogActive && !uiProperties.isPersistent) {
        const timer = setTimeout(() => {
            dispatch(hideOverlayDialog());
            try {
                uiProperties.onAutoHide();
            } catch (error) {
                Log.e(TAG, 'onAutoHide is undefined', error);
            }
        }, OVERLAY_TIMEOUT);
        dispatch(setOverlayDialogTimerIdAction(timer));
    }
};

export const clearOverlayTaskController = () => (dispatch: Function, getState: Function) => {
    const { previousScreenTaskController } = getState().screens;
    dispatch(setScreenTaskController(previousScreenTaskController));
};

export const onMouseOutOverlayDialogOperations = () => (dispatch: Function, getState: Function) => {
    const { activeComponent } = getState().interactiveLayer;
    if (activeComponent.length > 0) dispatch(clearActiveInteractiveComponent());
    dispatch(overlayEnableGhostMode());
};

export const onMouseOverOverlayDialogOperations =
    () => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;
        const { uiProperties } = getState().overlayDialog;
        if (value === uiProperties.mouseTrapValue) return;
        if (
            value === InteractiveComponents.EAST ||
            value === InteractiveComponents.WEST ||
            value === InteractiveComponents.SOUTH ||
            value === InteractiveComponents.NORTH
        ) {
            dispatch(setActiveInteractiveComponent(value));
            return;
        }
        const overlaySetIsCaretVisible =
            uiProperties.enableCaret &&
            uiProperties.options[value]?.horizontalSelectionoptions?.length > 1;
        dispatch(overlayDialogSetIsCaretVisibleAction(overlaySetIsCaretVisible));
        dispatch(setOverlayHighlightVerticalPositionAction(Number(value)));
        dispatch(overlayDisableGhostMode());
    };

export const overlayDialogSetCaretVisible = () => (dispatch: Function) => {
    dispatch(overlayDialogSetIsCaretVisibleAction(true));
};

export const overlayDialogSetCaretInvisible = () => (dispatch: Function, getState: Function) => {
    const { isCaretVisible } = getState().overlayDialog;
    if (isCaretVisible) dispatch(overlayDialogSetIsCaretVisibleAction(false));
};

export default overlayDialogReducer;
